<template>
  <div class="map-container">
    <MglMap
      :map-style.sync="mapStyle"
      :center="center"
      :zoom="zoom"
      :attribution-control="false"
      @load="onMapLoad"
    >
      <MglScaleControl 
        position="top-right"
        unit="nautical"
      />
      <MglNavigationControl position="top-left" />
      <div 
        v-if="mouseLatitude && mouseLongitude" 
        id="latlng-info"
      >
        <span
          v-if="latLngInfo.windSpeed"
          class="wind-info"
        ><font-awesome-icon icon="wind" />{{ latLngInfo.windSpeed }}kt {{ degreesToBearing (latLngInfo.windDir) }}</span>
        <span>{{ convertDMS(mouseLatitude, mouseLongitude) }}</span>
      </div>

      <wind-layer 
        v-if="showWind"
        :race-time="raceTime"
        :forecast-time="forecastTime"
        :is-pre-race="this.isPreRace"
      />

      <start-line-layer
        v-if="raceId===145"
      />

      <finish-line-layer
        v-if="raceId===145"
      />

      <rhumb-layer 
        v-if="showRhumbLine" 
        :race-id="raceId"
      />

      <buoy-layer 
        v-if="showBuoys"
        :race-id="raceId"
      />

      <yacht-layer 
        v-if="showAllYachts"
        :fleet-data="fleetData"
        :race-id="raceId"
        :race-time="raceTime"
        :show-yacht-names="showYachtNames"
      />
    </MglMap>
  </div>
</template>

<script>
import Mapbox from 'mapbox-gl'
import { MglMap, MglNavigationControl, MglScaleControl } from 'vue-mapbox'

import { library } from '@fortawesome/fontawesome-svg-core'
import { faWind } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

library.add(faWind)

import { convertDMS } from '../../util/boatUtil'

import YachtLayer from './YachtLayer'
import RhumbLayer from './RhumbLayer'
import StartLineLayer from './StartLineLayer'
import FinishLineLayer from './FinishLineLayer'
import WindLayer from './WindLayer'
import BuoyLayer from './BuoyLayer'

export default {
  components: {
    MglMap,
    MglNavigationControl,
    MglScaleControl,
    YachtLayer,
    RhumbLayer,
    StartLineLayer,
    FinishLineLayer,
    BuoyLayer,
    WindLayer,
    FontAwesomeIcon
  },
  props: {
    raceId: {
      type: Number,
      default: 0
    },
    raceTime: {
      type: String,
      default: ''
    },
    forecastTime: {
      type: Number,
      default: 0
    },
    fleetData: {
      type: Array,
      default: () => []
    },
    isPreRace: {
      type: Boolean,
      default: false
    },
    options: {
      type: Object,
      default: () => {
        return {
          showAllYachts: true,
          showYachtNames: true,
          showWind: false,
          showRhumbLine: true,
          showBuoys: true,
          center: [150.15, -38.31],
          zoom: 5,
          fleetBounds: { minLon: null, minLat: null, maxLon: null, maxLat: null }
        }
      }
    }
  },
  data() {
    return {
      latLngInfo: {
        lat: '',
        lng: '',
        windSpeed: null,
        windDir: null
      },
      mapStyle: 'https://maps.cycaracing.com/styles/klokantech-basic/style.json',
    }
  },
  computed: {
    center () {
      return this.options.center
    },
    zoom () {
      return this.options.zoom
    },
    showRhumbLine () {
      return this.options.showRhumbLine
    },
    showBuoys () {
      return this.options.showBuoys
    },
    showAllYachts () {
      return this.options.showAllYachts
    },
    showYachtNames () {
      return this.options.showYachtNames
    },
    showWind () {
      return this.options.showWind
    },
    mouseLatitude () {
      return this.latLngInfo.lat
    },
    mouseLongitude () {
      return this.latLngInfo.lng
    }
  },
  watch: {
    'options.fleetBounds' (newValue, oldValue) {
      if (newValue.minLon && 
          newValue.minLat &&
          newValue.maxLon &&
          newValue.maxLat) {
        this.map.fitBounds([[
          newValue.minLon,
          newValue.minLat
        ], [
          newValue.maxLon,
          newValue.maxLat
        ]])
      }
    }
  },
  created () {
    this.mapbox = Mapbox
    this.map = null
  },
  methods: {
    degreesToBearing (degrees) {
      const ref = ['W', 'WNW', 'NW', 'NNW', 'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W']

      const index = Math.round((degrees % 360) / 22.5)

      return ref[index]
    },
    onMapLoad (event) {
      this.map = event.map
      this.map.on('mousemove', (e) => {
        this.latLngInfo.lat = e.lngLat.lat
        this.latLngInfo.lng = e.lngLat.lng

        const width = 20
        const height = 20
        const features = this.map.queryRenderedFeatures([
          [e.point.x - width / 2, e.point.y - height / 2],
          [e.point.x + width / 2, e.point.y + height / 2]
        ])

        if (features.length > 1 && features[0].properties.speed) {
          this.latLngInfo.windSpeed = features[0].properties.speed.toFixed(0)
          this.latLngInfo.windDir = features[0].properties.direction.toFixed(0) % 360
        }
        else {
          this.latLngInfo.windSpeed = null
          this.latLngInfo.windDir = null
        }
      })
      this.map.on('mouseout', (e) => {
        this.latLngInfo.lat = ''
        this.latLngInfo.lng = ''
      })
      this.zoomToBoats()
      this.addAttribution()
    },

    convertDMS (lat, lng) {
      return convertDMS(lat, lng)
    },

    zoomToBoats () {
      if (this.options.fleetBounds.minLon && 
          this.options.fleetBounds.minLat &&
          this.options.fleetBounds.maxLon &&
          this.options.fleetBounds.maxLat) {
        this.map.fitBounds([[
          this.options.fleetBounds.minLon,
          this.options.fleetBounds.minLat
        ], [
          this.options.fleetBounds.maxLon,
          this.options.fleetBounds.maxLat
        ]])
      }
    },

    addAttribution () {
      this.map.addControl(new this.mapbox.AttributionControl({
        compact: false
      }), 'top-right')
    }
  }
}
</script>

<style scoped>
  .map-container {
    width: 100%;
    height: 100%;
    position:absolute;
    top:0;
    bottom:0;
  }

   #latlng-info {
    display: block;
    font-family: Din_reg, Arial;
    position: absolute;
    padding: 10px;
    border: none;
    border-radius: 3px;
    font-size: 12px;
    text-align: center;
    color: #222;
    background: #fff;
    bottom: 80px;
    right: 20px;
  }

  #latlng-info .wind-info {
    font-family: Din_Med, Arial;
    font-weight: 700;
    margin-right: 10px;
  }

  @media screen and (max-width: 480px) {
    #latlng-info {
      bottom: 110px;
    }
  }

  .text-monospace {
    font-family:monospace;
  }
</style>

